<script lang="ts">
  import Fa from 'svelte-fa';
  import { alertSevere } from '#lib/icons/index.js';
  import { type Snippet } from 'svelte';
  import { firstOrdinaryRowIndex, formatHeaderTooltip } from './utils';
  import { getContext } from '#lib/utils/svelte-context.js';
  import { DataTableContextKey } from './types';
  import SortingIcon from './SortingIcon.svelte';

  import type { Column } from '#lib/zod/output/TabularData.js';

  interface Props {
    quickSelect?: Snippet;
    checkbox?: Snippet;
    name?: Snippet;

    onColumnClicked?: (column: Column | 'name' | 'count') => void;
  }

  let { quickSelect, checkbox, name, onColumnClicked }: Props = $props();

  const { tableState, cols, flatCols, hasAlerts, showCheckboxes, borders, tooltip } = $derived(
    getContext(DataTableContextKey),
  );

  let drawColumnGroups = $derived(cols.some((c) => 'columns' in c));
  let firstColspan = $derived(firstOrdinaryRowIndex(true, showCheckboxes, hasAlerts));

  const thickBorder = 'not-last:border-r-2';
</script>

<!-- Draw Column Groups -->
{#if drawColumnGroups}
  <tr class="col-header-groups">
    <th colspan={firstColspan} class={['min-w-0 overflow-hidden text-ellipsis', thickBorder]}>
      {@render quickSelect?.()}
    </th>
    {#each cols as col, i (i)}
      {#if 'columns' in col}
        <th
          colspan={col.columns.length}
          class={['min-w-0 overflow-hidden text-ellipsis', thickBorder]}
        >
          {col.title}
        </th>
      {:else}
        <th class={[borders.has(i + 1) && thickBorder]}></th>
      {/if}
    {/each}
  </tr>
{/if}

<!-- Draw Column Headers -->
<tr class="col-header-titles">
  {#if showCheckboxes}
    <th class={[borders.has(1) && thickBorder]}>
      {@render checkbox?.()}
    </th>
  {/if}
  {#if hasAlerts}
    <th class="threshold-alert"><Fa icon={alertSevere} /></th>
  {/if}
  <th class={['col-title left', borders.has(firstColspan) && thickBorder]}>
    <div class="inline-flex w-full min-w-0 flex-col pl-0">
      <button
        class={['text-left', onColumnClicked && 'cursor-pointer']}
        onclick={() => onColumnClicked?.('name')}
      >
        {@render name?.()}
      </button>
      {#if !drawColumnGroups}
        {@render quickSelect?.()}
      {/if}
    </div>
  </th>
  {#each flatCols as col, i (i)}
    <th
      onclick={() => onColumnClicked?.(col)}
      onmouseenter={() => tooltip.set({ text: formatHeaderTooltip(col) })}
      onmouseleave={() => tooltip.set(undefined)}
      class={[
        'col-title select-none',
        borders.has(i + firstColspan + 1) && thickBorder,
        onColumnClicked && 'cursor-pointer',
      ]}
    >
      <div class="flex min-w-0 items-center justify-center gap-0.5">
        {#if !('key' in col) && col.type !== 'parameter' && col.type !== 'date-like-parameter'}
          {col.title ?? '?'}
        {:else}
          <span class="overflow-hidden text-ellipsis">
            {#if 'key' in col}
              {col.title ?? col.key}
            {:else if col.type === 'parameter' || col.type === 'date-like-parameter'}
              {col.title ?? col.parameter}
            {/if}
          </span>
          <span class="shrink-0">
            <SortingIcon sorting={tableState.getSortingState(i)} {col} />
          </span>
        {/if}
      </div>
    </th>
  {/each}
</tr>
